'use client';

import clsx from 'clsx/lite';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import type { MenuItem } from '~/types/graphql';

export function DesktopMenu({
  children,
  items,
  team,
  allStarCupMenuItems,
}: {
  children?: React.ReactNode;
  items: MenuItem[];
  team?: string;
  allStarCupMenuItems: MenuItem[];
}) {
  const pathname = usePathname();

  // if first segment is all-star-cup, set active menu to all star cup
  const [activeMenu, setActiveMenu] = useState<
    'Regular Season' | 'All-Star Cup'
  >(
    pathname?.split('/')[1] === 'all-star-cup'
      ? 'All-Star Cup'
      : 'Regular Season',
  );
  const inactiveMenu =
    activeMenu === 'All-Star Cup' ? 'Regular Season' : 'All-Star Cup';

  const menuItems = activeMenu === 'All-Star Cup' ? allStarCupMenuItems : items;

  return (
    <nav className="block w-auto" aria-label="header menu">
      {children}
      <ul className="flex min-w-[163px] flex-row gap-x-2 text-[16px] font-medium leading-none">
        {menuItems.map((item, i) => (
          <DesktopMenuItem key={i} item={item} team={team} />
        ))}

        {!team && (
          <li id="season-allstar-toggle" className="group relative w-full">
            <button
              className={clsx(
                'flex h-[44px] w-full min-w-[163px] flex-nowrap items-center justify-between gap-x-2 whitespace-nowrap rounded-lg px-4 font-normal',
                'bg-[#091724] text-white/[.64] hover:text-white',
              )}
            >
              <span className="label">{activeMenu}</span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="7"
                viewBox="0 0 12 7"
                className={clsx(
                  'mb-px stroke-current transition-transform duration-300',
                  'group-hover:rotate-180',
                )}
                fill="none"
              >
                <path
                  d="M1.51758 0.979492L6.09794 5.97949L10.6783 0.979492"
                  strokeWidth="1.52679"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <ul
              className={clsx(
                'absolute -inset-x-2 top-full z-10 m-0 hidden w-auto min-w-min flex-col gap-2 rounded-lg bg-[--theme-bg] p-2 shadow',
                'group-hover:!flex',
              )}
            >
              <li>
                <button
                  className={clsx(
                    'flex h-16 w-full flex-nowrap items-center gap-x-2 whitespace-nowrap rounded-lg px-4 font-normal',
                    'bg-[#091724] text-white/[.64] hover:text-white',
                  )}
                  onClick={() => setActiveMenu(inactiveMenu)}
                >
                  <span className="label">{inactiveMenu}</span>
                </button>
              </li>
            </ul>
          </li>
        )}
      </ul>
    </nav>
  );
}

function DesktopMenuItem({ item, team }: { item: MenuItem; team?: string }) {
  const pathname = usePathname();

  const isActive =
    // remove trailing slashes
    pathname?.replace(/\/$/, '') === item.uri?.replace(/\/$/, '');

  return (
    <li className="group relative">
      <Link
        href={item.uri ?? ''}
        className={clsx(
          'flex h-[44px] flex-nowrap items-center gap-x-2 rounded-lg px-4 font-[600]',
          team ? 'text-[--header-text]' : 'text-white',
          'group-hover:bg-white/10 [&:not(:has(ul:hover))]:hover:bg-white/10',
          isActive && 'bg-white/10',
        )}
      >
        <span className="label">{item.label ?? ''}</span>
        {!!item.childItems?.nodes?.length && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="7"
            viewBox="0 0 12 7"
            className={clsx(
              'mb-px stroke-current transition-transform duration-300',
              'group-hover:rotate-180',
            )}
            fill="none"
          >
            <path
              d="M1.51758 0.979492L6.09794 5.97949L10.6783 0.979492"
              strokeWidth="1.52679"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </Link>

      {!!item.childItems?.nodes?.length && (
        <ul
          className={clsx(
            'absolute -inset-x-2 top-full z-10 m-0 hidden flex-col gap-2 rounded-lg bg-[--theme-bg] p-2 shadow',
            'min-w-max whitespace-nowrap group-hover:!flex',
          )}
        >
          {item.childItems?.nodes?.map((childItem: MenuItem, i) => (
            <DesktopMenuItem key={i} item={childItem} team={team} />
          ))}
        </ul>
      )}
    </li>
  );
}
